<script>
   /**************************************************************/
   /* DataLoader for PCA/SIMCA app                               */
   /* -------------------------                                  */
   /* Loads test set from CSV file and returns its main output.   */
   /* If PCA model is provided it also applies the model and     */
   /* returns object with PCA results. In this case it will      */
   /* also recalculate the result object if array with outliers  */
   /* has been changed                                           */
   /**************************************************************/


   import { parseCSV, app_pca_predict, parseCSVParams, labelsByWidth, readSPCFiles }
      from '../../utils.js';
   import { FileSelector, Switch, Spinner, Select, Container, Button, TextInput } from 'svelte-controls-basic';
   import DatasetWidget from '../../widgets/DatasetWidget.svelte';


   let {
      WASM,                             // WASM object to make predictions if model is available (optional)
      model = null,                     // object with PCA model (optional)
      ncols = null,                     // columns a set must have, for the width guess without a model
      outliers = [],                    // array with outlier indices (optional)
      className = "Class",              // name of class related fields in the widget (e.g. 'Class' or 'Group')
      classMandatory = false,           // logical, if class column is mandatory in the data file
      classUnique = false,              // logical, if class labels must be all the same
      title = '',                       // title, to show on top of the block
      dataset = $bindable(),            // JSON with datasets
      resobj = $bindable(),             // JSON with PCA results (if model is provided)
      csvOnly = true                    // accept only CSV files or allow SPC as well
   } = $props();

   // prefix for the label ids the controls below are named by. it must be unique on the page, and
   // this component is mounted once per tab with every tab kept in the DOM, so it can not be
   // written out by hand
   const uid = $props.id();

   // possible delimeters and header options
   const sepOptions = [',', ';'];
   const headerOptions = $derived(model ? ['no', 'yes'] : ['no', 'labels', 'values']);

   // data properties
   let dataFile = $state();              // CSV file
   let varvaluesName = $state('');       // name of the variable values to be used as header
   let varvaluesUnits = $state('');      // units of the variable values

   // data properties, that will be autodetected from CSV file
   let sep = $state(',');                // seperator between values
   let header = $state('no');            // first row contains header (labels or values)
   let hasLabels = $state(false);        // first column contains row labels
   let hasClassLabels = $state(false);   // if data file contains class labels


   // promise, app status for handling and showing errors
   let promise = $state(null);
   let status = $state('neutral');
   let errMsg = $state('');

   // method for showing error
   function setError(msg) {
      status = 'error';
      errMsg = msg;
      console.error(msg)
   }

   // method for setting neutral state
   function setNeutral() {
      status = 'neutral';
      errMsg = '';
   }

   // method for setting success state
   function setSuccess() {
      status = 'success';
      errMsg = '';
   }


   // method to apply PCA model
   function predict(model, dataset, outliers) {
      promise = WASM()
         .then(Module => {
            resobj = app_pca_predict(Module, model, dataset, outliers, true);
            setSuccess();
         })
         .catch(e =>{
            resobj = null;
            setError(e);
         });
   };

   // a guess is read asynchronously and must not land after the file was changed, cleared or
   // loaded: every run of the 'dataFile' effect below takes the next number, and a read that
   // finishes with an older one is dropped
   let guessRun = 0;
   // set when the width rule in 'guessParams' turned the row-label switch on, until the next guess
   let labelsFromWidth = false;
   // the component going away retires a read in flight as well: an spc set landing after that
   // was written through the binding all the same
   $effect(() => () => { guessRun++; });

   // method for guessing CSV parameters
   function guessParams(dataFile, model) {

      // a label column taken from the last file's width must not cut the first variable off this
      // one, whether its guess fails or never runs; whatever the text guess set stays, as before
      if (labelsFromWidth) hasLabels = false;
      labelsFromWidth = false;

      // no data file is provided - no guessing
      if (!dataFile || dataFile.size < 10) return;
      const myRun = guessRun;

      // read first 100Kb
      const chunkSize = 100000;
      const first100KB = dataFile.slice(0, chunkSize);

      const reader = new FileReader();
      reader.onload = () => {
         if (myRun !== guessRun) return;
         const csvString = reader.result;
         if (!csvString || csvString.length === 0) return;

         // 'parseCSVParams' throws when it can not make sense of the chunk: too few rows, no
         // delimiter it detects, or a value it can not read as a number. this runs inside a
         // FileReader callback, so an error thrown here escapes into the event loop instead of
         // any caller, and without the catch the component said nothing about why the guess
         // did not happen. the chunk can be the reason on its own: a file with very wide rows
         // has its last sampled row cut at the boundary, so the message names the chunk and
         // does not repeat the parser's sentence, which speaks of the whole file
         try {
            let guessed, ncolsTotal, fewRowsPerClass;
            [sep, hasLabels, guessed, header, ncolsTotal, fewRowsPerClass] =
               parseCSVParams(csvString);
            // with no class column required, a class column in column 0 of three rows or fewer
            // per value on average is replicate row labels
            if (!classMandatory && fewRowsPerClass) {
               hasLabels = true;
               guessed = false;
            }
            hasClassLabels = (classMandatory || guessed);
            // numeric row labels the text guess missed, assumed from a row one column wider than
            // the model needs; not when the guess put the class in column 0
            if ((model || ncols) && !hasLabels && !guessed &&
                  labelsByWidth(ncolsTotal, dataCols(), hasClassLabels)) {
               hasLabels = true;
               labelsFromWidth = true;
            }
            if (model && header !== 'no') header = 'yes';
         } catch (e) {
            console.error(e);
            setError('Could not guess the CSV parameters from the first 100 KB of the file. ' +
               'Check the delimiter, header and row label settings and try to load it.');
         }
      };

      reader.onerror = function(e) {
         if (myRun !== guessRun) return;
         setError('Error reading CSV file. ' + e);
      };

      reader.readAsText(first100KB);
   }

   // the class switch moved by hand: the width rule counted no class column, so a label column it
   // assumed goes with the change
   function setClassLabels(v) {
      if (v === hasClassLabels) return;
      hasClassLabels = v;
      if (labelsFromWidth) hasLabels = false;
      labelsFromWidth = false;
   }

   // the number of data columns a set must have for the model, read by the guess and the check;
   // without a model, 'ncols', which only the guess reads
   function dataCols() {
      if (!model) return ncols;
      const prep = model.prep && Object.keys(model.prep).length > 0;
      return prep ? model.prep.npred : model.ncols + model.exclvars.length;
   }

   // checks a parsed set against the model and the settings it was loaded with, and takes it.
   // the spc path shares it: its set used to be taken with none of the checks, the width included
   function acceptData(d) {
      try {
         if (d.values.nrows < 1 ) {
            throw new Error('Dataset must have at least 1 row.');
         }

         if (model && d.values.ncols !== dataCols()) {
            throw new Error('Dataset has different number of columns than the training set.');
         }

         if (d.classNames.length > d.values.nrows / 2) {
            throw new Error('Dataset has too many unique ' + className.toLowerCase() + ' labels (' + d.classNames.length + '), check your CSV file.');
         }

         if (classMandatory && d.classNames.length === 0) {
            throw new Error('Dataset set must have a column filled with class names.');
         }

         if (classUnique && d.classNames.length > 1) {
            throw new Error('Dataset set must have a column filled with only single (target) class name.');
         }

         // if model is available inherit variable properties from the model
         if (model) {
            // if data values in the training set were in reverse order it is expected
            // that they are in the reverse order in this dataset as well
            d.varrev = model.varrev;
            d.header = model.header;
            d.varlabels = model.varlabels;
            d.varvalues = model.varvalues;
            d.varvaluesName = model.varvaluesName;
            d.varvaluesUnits = model.varvaluesUnits;
         }

         dataset = d;
         setSuccess();
      } catch (e) {
         // no 'dataset = null' here: the button and the spc read which reach this run only while
         // no data set is loaded, so the write cleared nothing and only turned the session's
         // initial 0 into null. that counted as a change for the 'dataFile' effect, which re-ran
         // for the same file and wiped this message with its 'setNeutral' before it was ever
         // shown, so the first failed load said nothing and only the second did
         setError(e);
      }
   }

   // method for parsing CSV content provided as one large string to dataset object.
   function setData(strData) {
      try{
         const d = parseCSV(
            strData,
            sep,
            header === 'yes' ? 'labels' : header, // when load data for prediction simpler interface is used
            hasLabels,
            hasClassLabels,
            varvaluesName,
            varvaluesUnits
         );
         d['fileName'] = dataFile.name.replace(/\.[^/.]+$/, "");
         acceptData(d);
      } catch (e) {
         // no 'dataset = null' here either, see 'acceptData'
         setError(e);
      }
   }

   // a file is a csv when its name says so. the browser fills 'type' from extension mappings of
   // its own or of the platform, and firefox up to esr 140 takes the platform's for '.csv',
   // where any installed application may have registered the extension under a type of its
   // choosing; the selector next to this already accepts files by their extension. an Array
   // and the empty string have no 'name'
   const isCSVFile = f => !!f && typeof f.name === 'string' && f.name.toLowerCase().endsWith('.csv');

   // a read in progress: the selector and the settings give way to a spinner until the data
   // lands or the read fails, and both of those set the status again
   function setLoading() {
      status = 'loading';
      errMsg = '';
   }

   // promise for reading the CSV file
   function readCSV () {
      setLoading();
      // a guess still in flight is retired: it would land during the read and write the
      // delimiter, or the status, over it
      guessRun++;
      const reader = new FileReader();
      reader.onload = () => { setData(reader.result) };
      // without this a file which can not be read would leave the spinner up for good
      reader.onerror = () => {
         const reason = reader.error ? reader.error.message : 'unknown error';
         setError('Error reading CSV file. ' + reason);
      };
      reader.readAsText(dataFile);
   }

   // method for reading the SPC file. the read is retired like a guess, by the next run of the
   // 'dataFile' effect and by the teardown of 'guessRun': its set used to land after the file
   // had been changed or cleared, or after the component had gone
   async function readSPC () {
      setLoading();
      const myRun = guessRun;
      try {
         // several files arrive as a plain Array, from the dialog and from a drop alike since
         // svelte-controls-basic 3.2.0; only a single File has to be wrapped
         const dataFiles = Array.isArray(dataFile) ? dataFile : [dataFile];
         const d = await readSPCFiles(dataFiles);
         if (myRun !== guessRun) return;
         acceptData(d);
      } catch (e) {
         if (myRun !== guessRun) return;
         setError('Failed to read SPC file: ' + e);
      }
   }

   // reset whole component to initial state of the component
   function reset () {
      dataFile = '';
      dataset = null;
      resobj = null;
      varvaluesName = '';
      varvaluesUnits = '';
      setNeutral();
   }

   // if data file is selected - guess parameters
   $effect(() => {
      guessRun++;

      // undefined is the initial value, before the selector wrote anything; the 0 that used to
      // be tested here as well is a value it never holds
      if (dataFile === undefined || dataset) return;

      // if data file is empty reset status and error messages
      if (dataFile === null || dataFile === '') {
         reset();
         return;
      }

      // dataFile holds several files: a plain Array, from the dialog and from a drop alike
      if (Array.isArray(dataFile)) {

         // 'v' is a File: 'v.slice()' is Blob.slice and returns a Blob, never the extension, so
         // the name has to be taken explicitly. the selector accepts '.SPC' as well
         if (!dataFile.every(v => v.name.toLowerCase().endsWith('.spc'))) {
            setError('Multiple file selection is allowed only for SPC files.');
            return;
         }

         readSPC();
         return;
      }

      if (isCSVFile(dataFile)) {
         guessParams(dataFile, model);
         setNeutral();
         return;
      }

      // only csv was offered, so a file of another kind is refused rather than tried as spc
      if (csvOnly) {
         setError('The file has to be a CSV file.');
         return;
      }

      readSPC();
   });

   // if separator was changed remove error message
   $effect(() => {
      if (sep) {
         setNeutral();
      }
   });

   // if dataset, model or array with outliers have been changed, make new predictions
   $effect(() => {
      if (dataset && model && !resobj && !errMsg) {
         predict(model, dataset, outliers);
      }
   });
</script>

{#if status === 'loading'}

   <!-- the file is read asynchronously, so the spinner is in the DOM before the parse, which
        runs on the main thread. whether a frame is painted in between is the browser's call -->
   <Container>
      <Spinner />
      <p>Loading data...</p>
   </Container>

{:else if !dataset }

   <!-- show if data is not loaded -->
   <Container>
      {#if csvOnly}
      <FileSelector
         message="Select CSV file with dataset"
         bind:file={dataFile}
      />
      {:else}
      <FileSelector
         message="Select CSV or SPC file with dataset"
         acceptType=".csv, .spc"
         multiple={true}
         bind:file={dataFile}
      />
      {/if}
   </Container>

   <!-- show if file is selected but data is not loaded -->
   {#if isCSVFile(dataFile)}
   <Container label="Delimiter" id="{uid}-sep">
      <Select bind:value={sep} options={sepOptions} />
   </Container>
   <Container label="Row labels" id="{uid}-haslabels">
      <Switch bind:value={hasLabels} options={['no', 'yes']} />
   </Container>
   {#if !classMandatory}
   <Container label="{className} labels" id="{uid}-hasclasslabels">
      <Switch bind:value={() => hasClassLabels, setClassLabels} options={['no', 'yes']} />
   </Container>
   {/if}
   <Container label="Header" id="{uid}-header">
      <Select bind:value={header} options={headerOptions} />
   </Container>
   {#if header==='values'}
   <Container label="Header name" id="{uid}-varvaluesname">
      <TextInput bind:value={varvaluesName} placeholder="e.g. Wavelength" />
   </Container>
   <Container label="Header units" id="{uid}-varvaluesunits">
      <TextInput bind:value={varvaluesUnits} maxLength={10} placeholder="e.g. nm" />
   </Container>
   {/if}
   <Container label="&nbsp;">
      <Button text='Load' onclick={readCSV}/>
   </Container>
   {/if}

{:else}


   {#if promise}

      <!-- calculation is in process -->
      {#await promise}
         <Container>
            <Spinner />
            <p>{model ? 'Loading data and apply model...' : 'Loading data...'}</p>
         </Container>
      {/await}

   {/if}

{/if}

{#if dataset }
   <!-- dataset is loaded - show info and cancel button -->
   <DatasetWidget {dataset} {reset} {title} />
{/if}

<!-- error message -->
{#if status === 'error' || errMsg}
<Container status="error">
   <p>{errMsg}</p>
</Container>
{/if}

